<template>
  <b-card>
    <rc-overlay :show="isTeamSectionBusy">
      <div>
        <h4 class="mb-1">
          {{ $t('order.team.title') + (teamCardTitle ? ': ' + teamCardTitle : '') }}
          <b-dropdown
            v-if="showTeamActionsMenu"
            v-visible-to-role="['ROLE_DISPATCHER']"
            variant="flat-secondary"
            size="sm"
            right
            class="float-right mt-n50 mr-n1"
            toggle-class="btn btn-flat-primary btn-sm order-team-card__menu-toggle"
            no-caret
          >
            <template #button-content>
              <feather-icon icon="MoreVerticalIcon" />
            </template>
            <b-dropdown-item
              v-if="canAddSupportingTeam"
              @click="startAssignSupportingTeam"
            >
              <feather-icon
                icon="PlusIcon"
                size="16"
                class="mr-50 text-primary"
              />
              <span>{{ $t('order.team.add-team') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canChangeTeam"
              @click="changeTeam"
            >
              <feather-icon
                icon="RepeatIcon"
                size="16"
                class="mr-50 text-warning"
              />
              <span>{{ $t('order.team.change-team') }}</span>
            </b-dropdown-item>
            <b-dropdown-item
              v-if="canReleaseTeam"
              @click="startRelease"
            >
              <feather-icon
                icon="XCircleIcon"
                size="16"
                class="mr-50 text-danger"
              />
              <span>{{ releaseLabel }}</span>
            </b-dropdown-item>
          </b-dropdown>
        </h4>
      </div>

      <div v-if="order.team.id">
        <dl
          v-if="showCombinedPrimaryCrew"
          class="row mb-0"
        >
          <dt class="font-bold col-5">
            {{ combinedCrewLabel }}
          </dt>
          <dd class="col-7">
            {{ formatUserName(order.team.driverUser) }}
          </dd>
        </dl>
        <dl
          v-if="order.team.paramedicUser && !showCombinedPrimaryCrew"
          class="row mb-0"
        >
          <dt class="font-bold col-5">
            {{ $t('order.team.label.paramedic-user') }}
          </dt>
          <dd class="col-7">
            {{ formatUserName(order.team.paramedicUser) }}
          </dd>
        </dl>
        <dl
          v-if="!showCombinedPrimaryCrew"
          class="row mb-0"
        >
          <dt class="font-bold col-5">
            {{ $t('order.team.label.driver-user') }}
          </dt>
          <dd class="col-7">
            {{ formatUserName(order.team.driverUser) }}
          </dd>
        </dl>
        <dl
          v-if="!isDriverOnlyTeam"
          class="row mb-0"
        >
          <dt class="font-bold col-5 mb-1">
            {{ $t('order.doctor.title') }}
            <feather-icon
              :icon="selectDoctor?'XIcon':'Edit2Icon'"
              class="ml-50 cursor-pointer text-primary"
              @click="selectDoctor = !selectDoctor"
            />
          </dt>
          <dd class="col-7">
            <span v-if="order.doctorUser && !selectDoctor">
              {{ order.doctorUser.surname }} {{ order.doctorUser.name }}
            </span>
            <order-doctor-selector
              v-if="selectDoctor"
              :order="order"
              @doctor:selected="doctorSelected"
            />
          </dd>
        </dl>
        <dl class="row mb-0">
          <dt class="font-bold col-5">
            {{ $t('order.team.label.vehicle') }}
          </dt>
          <dd class="col-7">
            {{ order.team.vehicle.sideNumber }}<span
              v-if="isVehicleDeleted(order.team.vehicle)"
              class="text-muted"
            > ({{ $t('conf.vehicle.deleted-suffix') }})</span>
          </dd>
        </dl>
        <div
          v-if="supportingTeams.length"
          class="mt-1"
        >
          <div class="font-small-3 text-uppercase text-secondary mb-50">
            {{ $t('order.team.supporting-title') }}
          </div>
          <div
            v-for="assignment in supportingTeams"
            :key="assignment.id || assignment.team.id"
            class="border rounded p-50 mb-50"
          >
            <div class="d-flex align-items-start justify-content-between">
              <div>
                <div class="font-weight-bolder">
                  {{ assignment.team.teamDefinition.code }}
                </div>
                <div class="font-small-2 text-secondary">
                  {{ formatTeamMembers(assignment.team) }}
                </div>
                <div
                  v-if="assignment.team.vehicle && assignment.team.vehicle.sideNumber"
                  class="font-small-2 text-secondary mt-25"
                >
                  {{ $t('order.team.label.vehicle') }}: {{ assignment.team.vehicle.sideNumber }}<span
                    v-if="isVehicleDeleted(assignment.team.vehicle)"
                    class="text-muted"
                  > ({{ $t('conf.vehicle.deleted-suffix') }})</span>
                </div>
              </div>
              <b-button
                v-if="canReleaseSupportingTeams"
                size="sm"
                variant="outline-danger"
                @click="releaseSupportingTeam(assignment.team.id)"
              >
                {{ $t('order.team.release-supporting') }}
              </b-button>
            </div>
          </div>
        </div>
      </div>

      <div v-else-if="plannedTeamDefinition || order.isFirstAvailablePlanning">
        <div class="mb-1">
          <div class="d-flex align-items-start justify-content-between mb-1">
            <div>
              <div class="font-small-3 text-uppercase text-secondary mb-25">
                {{ $t('order.team.label.planned-team') }}
              </div>
              <div class="h4 text-primary mb-0 font-weight-bolder">
                {{ plannedTeamDefinition }}
              </div>
              <b-badge
                v-if="order.isFirstAvailablePlanning"
                variant="light-warning"
                class="mt-25"
              >
                {{ $t('order.planning.first-available') }}
              </b-badge>
            </div>
          </div>

          <div class="row">
            <div
              v-if="order.plannedAssignmentOffsetMinutes !== null"
              class="col-sm-6 mb-75"
            >
              <div class="text-secondary font-small-2 mb-25">
                {{ $t('order.planning.assignment-at') }}
              </div>
              <div class="font-weight-bolder">
                {{ formatDurationMinutes(order.plannedAssignmentOffsetMinutes) }}
              </div>
            </div>

            <div
              v-if="order.plannedDurationMinutes"
              class="col-sm-6 mb-75"
            >
              <div class="text-secondary font-small-2 mb-25">
                {{ $t('order.planning.duration-minutes') }}
              </div>
              <div class="font-weight-bolder">
                {{ formatDurationMinutes(order.plannedDurationMinutes) }}
              </div>
            </div>
          </div>
        </div>
      </div>

      <div
        v-if="!loading"
        v-visible-to-role="['ROLE_DISPATCHER']"
      >

        <order-team-selector
          ref="teamSelector"
          :order="order"
          :workflow="workflow"
          :show-action-buttons="false"
          @busy-change="handleSelectorBusyChange"
        />

      </div>
    </rc-overlay>
  </b-card>
</template>
<script>
import RcOverlay from '@/layouts/components/rc/RcOverlay.vue'
import OrderTeamSelector from '@/views/orders/partials/OrderTeamSelector.vue'
import OrderDoctorSelector from '@/views/orders/partials/OrderDoctorSelector.vue'
import { parseRequestError, transformDatetimeToHumanReadablePretty, isVehicleDeleted } from '@/helpers/helpers'
import { formatPlanningMinutes } from '@/helpers/orderPlanning'
import {
  BBadge, BButton, BCard, BDropdown, BDropdownItem,
} from 'bootstrap-vue'
import guard from '@/guard'

export default {
  name: 'OrderTeamCard',
  components: {
    BBadge,
    BButton,
    BCard,
    BDropdown,
    BDropdownItem,
    RcOverlay,

    OrderTeamSelector,
    OrderDoctorSelector,
  },
  props: {
    loading: {
      type: Boolean,
      default: false,
    },
    order: {
      type: Object,
      default: () => {},
    },
  },
  data() {
    return {
      selectDoctor: false,
      selectorBusy: false,
      supportingTeamActionLoading: false,
    }
  },
  computed: {
    workflow() {
      return this.$store.getters['order/getOrderWorkflow']
    },
    isDriverOnlyTeam() {
      return Boolean(this.order && this.order.team && this.order.team.teamDefinition && this.order.team.teamDefinition.driverOnly)
    },
    teamCardTitle() {
      if (this.assignedTeamDefinition) return this.assignedTeamDefinition
      if (this.plannedTeamDefinition) return this.plannedTeamDefinition
      if (this.order?.isFirstAvailablePlanning) return this.$t('order.planning.first-available')
      return ''
    },
    assignedTeamDefinition() {
      return this.getTeamDefinition(this.order && this.order.team ? this.order.team : null)
    },
    plannedTeamDefinition() {
      return this.getTeamDefinition(this.order && this.order.plannedTeamDefinition ? { teamDefinition: this.order.plannedTeamDefinition } : null)
    },
    showCombinedPrimaryCrew() {
      return !this.isDriverOnlyTeam && this.isSameUser(this.order?.team?.paramedicUser, this.order?.team?.driverUser)
    },
    combinedCrewLabel() {
      return `${this.$t('order.team.label.paramedic-user')} / ${this.$t('order.team.label.driver-user')}`
    },
    supportingTeams() {
      const assignments = Array.isArray(this.order?.teamAssignments) ? this.order.teamAssignments : []
      return assignments.filter(assignment => assignment && assignment.team && !assignment.isMaster && !assignment.releasedAt)
    },
    canReleaseSupportingTeams() {
      return guard.hasAtLeastOneRole(this.$store.getters['auth/getUser']?.roles || [], ['ROLE_DISPATCHER'])
        && !!this.workflow?.teamActions?.release?.allowed
        && !!this.order?.id
        && Number.isFinite(Number(this.order?.version))
    },
    canManageTeamActions() {
      return guard.hasAtLeastOneRole(this.$store.getters['auth/getUser']?.roles || [], ['ROLE_DISPATCHER'])
    },
    canAddSupportingTeam() {
      return this.canManageTeamActions
        && !!this.order?.team?.id
        && !!this.workflow?.teamActions?.assign?.allowed
    },
    canChangeTeam() {
      return this.canManageTeamActions
        && !!this.order?.team?.id
        && (
          !!this.workflow?.teamActions?.assign?.allowed
          || !!this.workflow?.teamActions?.transfer?.allowed
        )
    },
    canReleaseTeam() {
      return this.canManageTeamActions
        && !!this.order?.team?.id
        && !!this.workflow?.teamActions?.release?.allowed
    },
    releaseLabel() {
      const labelKey = this.workflow?.teamActions?.release?.labelKey

      if (labelKey && typeof this.$te === 'function' && this.$te(labelKey)) {
        return this.$t(labelKey)
      }

      return this.$t('order.team.unbind-team')
    },
    showTeamActionsMenu() {
      return this.canAddSupportingTeam || this.canChangeTeam || this.canReleaseTeam
    },
    isTeamSectionBusy() {
      return this.loading || this.selectorBusy || this.supportingTeamActionLoading
    },
  },
  methods: {
    transformDatetimeToHumanReadablePretty,
    isVehicleDeleted,
    formatUserName(user) {
      if (!user) {
        return ''
      }

      return `${user.surname || ''} ${user.name || ''}`.trim()
    },
    isSameUser(firstUser, secondUser) {
      if (!firstUser || !secondUser) {
        return false
      }

      if (firstUser.id && secondUser.id) {
        return firstUser.id === secondUser.id
      }

      return this.formatUserName(firstUser) === this.formatUserName(secondUser)
    },
    formatTeamMembers(team) {
      if (!team) {
        return ''
      }

      if (this.isSameUser(team.paramedicUser, team.driverUser)) {
        return `${this.combinedCrewLabel}: ${this.formatUserName(team.driverUser)}`
      }

      const parts = []
      if (team.paramedicUser) {
        parts.push(`${this.$t('order.team.label.paramedic-user')}: ${this.formatUserName(team.paramedicUser)}`)
      }
      if (team.driverUser) {
        parts.push(`${this.$t('order.team.label.driver-user')}: ${this.formatUserName(team.driverUser)}`)
      }

      return parts.filter(Boolean).join(' | ')
    },
    formatDurationMinutes(value) {
      return formatPlanningMinutes(value)
    },
    doctorSelected() {
      this.selectDoctor = false
    },
    handleSelectorBusyChange(value) {
      this.selectorBusy = value
    },
    startAssignSupportingTeam() {
      this.$refs.teamSelector?.startAssignSupportingTeam()
    },
    changeTeam() {
      this.$refs.teamSelector?.changeTeam()
    },
    startRelease() {
      this.$refs.teamSelector?.startRelease()
    },
    getTeamDefinition(team) {
      if (team && team.teamDefinition && team.teamDefinition.code) {
        return team.teamDefinition.code
      }
      return ''
    },
    releaseSupportingTeam(teamId) {
      const requestVersion = Number(this.order?.version)

      if (!Number.isFinite(requestVersion)) {
        return
      }

      this.supportingTeamActionLoading = true
      this.$store.dispatch('order/updateOrderTeam', {
        id: this.order.id,
        action: 'release',
        data: {
          version: requestVersion,
          teamId,
          confirmation: false,
          reason: null,
        },
      })
        .then(() => {
          window.toast.notify.success(this.$t('order.notify.edit.success_title'))
          return this.$store.dispatch('order/fetchOrderById', this.order.id)
        })
        .catch(err => {
          parseRequestError(err)
        })
        .finally(() => {
          this.supportingTeamActionLoading = false
        })
    },
  },
}
</script>
